import { CalculatorValues, ChartDataPoint, RiskPreset, ASSET_ALLOCATION_DATA, createTranslatedAssetAllocationData } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

export function calculateCompoundInterest(values: CalculatorValues) {
  const r = values.expectedReturn / 100;
  const i = Math.pow(1 + r, 1 / 12) - 1; // månadsränta
  const N = values.savingsPeriod * 12;    // antal månader

  // Total insatt (utan ränta)
  const totalSavings = values.startingAmount + (values.monthlySavings * N);

  // Slutvärde
  let finalAmount: number;
  if (r === 0) {
    // hantera 0% ränta (undvik division med 0)
    finalAmount = totalSavings;
  } else {
    const fvPV = values.startingAmount * Math.pow(1 + i, N);
    const fvPMT = values.monthlySavings * ((Math.pow(1 + i, N) - 1) / i); // insättning i slutet av månaden
    // Om du vill räkna insättning i BÖRJAN av månaden: multiplicera fvPMT med (1 + i)
    finalAmount = fvPV + fvPMT;
  }

  const totalReturn = finalAmount - totalSavings;

  return {
    totalSavings,
    finalAmount,
    totalReturn
  };
}

export function generateChartData(values: CalculatorValues, translations: ToolsTranslations['savings']): ChartDataPoint[] {
  const r = values.expectedReturn / 100;
  const i = Math.pow(1 + r, 1 / 12) - 1;

  return Array.from({ length: values.savingsPeriod + 1 }, (_, year) => {
    const n = year * 12; // månader till och med detta år
    const contributedToDate = values.startingAmount + values.monthlySavings * n;

    let fvAtYear: number;
    if (r === 0) {
      fvAtYear = contributedToDate;
    } else {
      const fvPV = values.startingAmount * Math.pow(1 + i, n);
      const fvPMT = values.monthlySavings * ((Math.pow(1 + i, n) - 1) / i); // slutet av månaden
      fvAtYear = fvPV + fvPMT;
    }

    return {
      year: `${year} ${translations.yearsSuffix}`,
      'Sparande': contributedToDate,
      'Totalt': Math.round(fvAtYear),
      'Avkastning': Math.round(fvAtYear - contributedToDate),
    };
  });
}

export function getRiskLevel(expectedReturn: number, translations?: ToolsTranslations['savings']): RiskPreset {
  const returnPercent = expectedReturn / 100;

  // Always use translated data if available, otherwise create fallback presets
  let presets;
  if (translations) {
    const translatedData = createTranslatedAssetAllocationData(translations);
    presets = translatedData.presets;
  } else {
    // Create fallback presets with hardcoded Swedish text
    presets = {
      low: {
        ...ASSET_ALLOCATION_DATA.presets.low,
        name: "Låg risk",
        summary: "Stabil kapitalbevarande tillväxt",
        description: "Låg risk innebär stabila men lägre avkastningar. Perfekt för dig som prioriterar kapitalbevarande framför hög tillväxt."
      },
      medium: {
        ...ASSET_ALLOCATION_DATA.presets.medium,
        name: "Medelrisk",
        summary: "Balanserad tillväxt och stabilitet",
        description: "Medelrisk ger en balans mellan tillväxt och stabilitet. En bra kompromiss för de flesta sparare."
      },
      high: {
        ...ASSET_ALLOCATION_DATA.presets.high,
        name: "Hög risk",
        summary: "Högre avkastning med svängningar",
        description: "Hög risk kan ge större avkastningar men också större svängningar. Lämpligt för långsiktigt sparande."
      },
      very_high: {
        ...ASSET_ALLOCATION_DATA.presets.very_high,
        name: "Mycket hög risk",
        summary: "Maximal tillväxt hög risk",
        description: "Mycket hög risk kan ge högsta potentiella avkastningen men med betydande risk för förluster. Endast för erfarna investerare."
      }
    };
  }

  if (returnPercent <= 0.04) { // 0-4% = low risk
    return presets.low;
  } else if (returnPercent <= 0.08) { // 4-8% = medium risk
    return presets.medium;
  } else if (returnPercent <= 0.12) { // 8-12% = high risk
    return presets.high;
  } else { // >12% = very high risk
    return presets.very_high;
  }
}

export function getRiskColors(riskLevel: RiskPreset) {
  // Determine risk level based on expected return percentage
  // This is more reliable than comparing names which might be translated
  const expectedReturn = riskLevel.expected_return * 100;
  
  if (expectedReturn <= 4) {
    return {
      color: 'emerald',
      bgColor: 'emerald-50/70',
      borderColor: 'emerald-100'
    };
  } else if (expectedReturn <= 8) {
    return {
      color: 'blue',
      bgColor: 'blue-50/70',
      borderColor: 'blue-100'
    };
  } else if (expectedReturn <= 12) {
    return {
      color: 'orange',
      bgColor: 'orange-50/70',
      borderColor: 'orange-100'
    };
  } else {
    return {
      color: 'red',
      bgColor: 'red-50/70',
      borderColor: 'red-100'
    };
  }
}

